import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  Spinner,
} from "reactstrap";
import EmbedCard from "components/common/EmbedSnippets";
import { cardType, kindT, styleKind } from "./cardKind";
import { ErrorToaster, successToaster } from "components/common/toast";
import handleError from "utils/handleError";
import { imgUrl } from "utils/imgUrl";
import {
  AddMenuCardAPI,
  DeleteCardImageAPI,
  DeleteMenuCardAPI,
  GetMenuCardsAPI,
  ReorderCardsAPI,
  UpdateMenuCardAPI,
  UploadCardImageAPI,
  AICardsAPI,
} from "network/menuCards";
import AssignWinesModal from "./AssignWinesModal";
import AiPromptModal from "./AiPromptModal";
import { moveInList } from "./structureTree";

const GOLD = "#b8932f";
const STYLE = `
.mc-row{display:flex;align-items:center;gap:12px;padding:10px 8px;border-bottom:1px solid rgba(128,128,128,.25)}
.mc-handle{cursor:grab;font-size:1.3rem;line-height:1;padding:0 6px;user-select:none;color:${GOLD}}
.mc-thumb{width:64px;height:48px;object-fit:cover;border-radius:8px;flex:none;background:rgba(128,128,128,.2)}
.mc-info{flex:1;min-width:0}
.mc-actions{display:flex;flex-wrap:wrap;gap:6px;justify-content:flex-end}
.mc-actions .btn{margin:0}
.mc-off{opacity:.55}
.mc-upload{display:flex;align-items:center;gap:10px;padding:10px 14px;border:2px dashed ${GOLD};border-radius:10px;cursor:pointer;color:${GOLD};font-weight:600;margin:0;background:rgba(184,147,47,.08)}
.mc-upload:hover{background:rgba(184,147,47,.18)}
.mc-upload i{font-size:1.4rem}
.mc-upload small{color:inherit;font-weight:400;opacity:.85;word-break:break-all}
.mc-before{box-shadow:0 -3px 0 0 ${GOLD}}
.mc-after{box-shadow:0 3px 0 0 ${GOLD}}
`;

// Creazione e modifica di una carta: nome, descrizione e immagine di copertina.
const CardFormModal = ({ card, open, onClose, onSaved, kind: presetKind }) => {
  const { t } = useTranslation("cards");
  const [name, setName] = useState("");
  // Tipo della nuova carta: dalla sezione aperta (Drinks o Food); se si e' nell'elenco completo si sceglie qui.
  const [kind, setKind] = useState(presetKind || "drinks");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(card?.name || "");
      setKind(presetKind || "drinks");
      setDescription(card?.description || "");
      setFile(null);
      setRemoveImage(false);
    }
  }, [open, card]);

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) {
      ErrorToaster(t("form.nameTooShort"));
      return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description: description.trim(), ...(card ? {} : { kind }) };
      const res = card ? await UpdateMenuCardAPI(card.uuid, payload) : await AddMenuCardAPI(payload);
      const id = card?.uuid || res?.data?.uuid;
      try {
        if (file && id) await UploadCardImageAPI(id, file);
        else if (removeImage && id) await DeleteCardImageAPI(id);
      } catch (err) {
        ErrorToaster(t("form.imageFailed", { error: handleError(err?.status_code, err?.message) }));
        onSaved();
        onClose();
        return;
      }
      successToaster(card ? t("form.updated") : t("form.created"));
      onSaved();
      onClose();
    } catch (err) {
      ErrorToaster(handleError(err?.status_code, err?.message));
    } finally {
      setSaving(false);
    }
  };

  const hasImage = !!card?.image && !removeImage && !file;

  return (
    <Modal isOpen={open} toggle={onClose} className="modal-md">
      <div className="modal-header">
        <h5 className="modal-title">{card ? t("form.editTitle") : t("form.newTitle")}</h5>
        <button aria-label={t("close")} className="close" type="button" onClick={onClose}>
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <Form onSubmit={submit}>
        <div className="modal-body">
          {!card && !presetKind && (
            <FormGroup>
              <Label>{t("form.kind")}</Label>
              <Input type="select" value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="drinks">Drinks Cards</option>
                <option value="food">Food Cards</option>
              </Input>
            </FormGroup>
          )}
          <FormGroup>
            <Label>{t("form.name")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("form.namePlaceholder")} autoFocus />
          </FormGroup>
          <FormGroup>
            <Label>{t("form.description")}</Label>
            <Input type="textarea" rows="3" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("form.optional")} />
          </FormGroup>
          <FormGroup>
            <Label>{t("form.cover")}</Label>
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              {hasImage && <img src={imgUrl(card.image)} alt="" className="mc-thumb" />}
              <label className="mc-upload">
                <i className="fas fa-cloud-upload-alt" aria-hidden="true"></i>
                <span>
                  {file ? t("form.changeImage") : card?.image ? t("form.replaceImage") : t("form.chooseImage")}
                  {file && <small className="d-block">{file.name}</small>}
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  style={{ display: "none" }}
                />
              </label>
            </div>
            {card?.image && !file && (
              <label className="mb-0 small mt-2 d-block">
                <input type="checkbox" checked={removeImage} onChange={(e) => setRemoveImage(e.target.checked)} /> {t("form.removeImage")}
              </label>
            )}
            <small className="text-muted d-block mt-1">{t("form.imageHint")}</small>
          </FormGroup>
        </div>
        <div className="modal-footer">
          <Button type="button" color="secondary" onClick={onClose} disabled={saving}>{t("cancel")}</Button>
          <Button type="submit" color="primary" disabled={saving}>{saving ? t("saving") : t("save")}</Button>
        </div>
      </Form>
    </Modal>
  );
};

const MenuCards = () => {
  const { t: baseT, i18n } = useTranslation("cards");
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [params] = useSearchParams();
  // ?type=menu mostra solo le carte del menu (cucina), ?type=drinks tutte le altre; senza filtro, tutte.
  const type = params.get("type");
  // Nelle Food Cards i testi parlano di piatti e menu.
  const t = kindT(baseT, i18n, type === "menu");
  const typeOf = cardType;
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [formCard, setFormCard] = useState(undefined); // undefined = chiuso, null = nuova, oggetto = modifica
  const [assignCard, setAssignCard] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [shareCard, setShareCard] = useState(null);
  const [alert, setAlert] = useState(null);
  const [over, setOver] = useState(null); // { index, pos } durante il trascinamento
  const dragFrom = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Tutte le carte in una pagina: servono tutte per poterle riordinare.
      const res = await GetMenuCardsAPI({ page: 1, perPage: 100, query: search, order: "position" });
      setCards(res?.data?.menu_cards || []);
    } catch (e) {
      ErrorToaster(handleError(e?.status_code, e?.message));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (card) => {
    setAlert(null);
    try {
      await DeleteMenuCardAPI(card.uuid);
      successToaster(t("list.deleted"));
      if (shareCard?.uuid === card.uuid) setShareCard(null);
      load();
    } catch (e) {
      ErrorToaster(handleError(e?.status_code, e?.message));
    }
  };

  const confirmDelete = (card) =>
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title={t("list.deleteTitle")}
        onConfirm={() => remove(card)}
        onCancel={() => setAlert(null)}
        confirmBtnBsStyle="success"
        cancelBtnBsStyle="danger"
        confirmBtnText={t("list.deleteConfirm")}
        cancelBtnText={t("cancel")}
        showCancel
      >
        {t("list.deleteBody", { name: card.name })}
      </ReactBSAlert>
    );

  const toggleHidden = async (card) => {
    try {
      const res = await UpdateMenuCardAPI(card.uuid, { is_active: !card.is_active });
      const active = res?.data?.is_active ?? !card.is_active;
      setCards((list) => list.map((c) => (c.uuid === card.uuid ? { ...c, is_active: active } : c)));
      successToaster(active ? t("list.shownAgain") : t("list.hiddenNow"));
    } catch (e) {
      ErrorToaster(handleError(e?.status_code, e?.message));
    }
  };

  // ---- Riordino con il trascinamento ----
  const endDrag = () => {
    dragFrom.current = null;
    setOver(null);
  };
  const onRowOver = (index) => (e) => {
    if (dragFrom.current == null) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const pos = e.clientY < r.top + r.height / 2 ? "before" : "after";
    setOver((prev) => (prev && prev.index === index && prev.pos === pos ? prev : { index, pos }));
  };
  const onDrop = async (e) => {
    const from = dragFrom.current;
    if (from == null || !over) return;
    e.preventDefault();
    const to = over.pos === "before" ? over.index : over.index + 1;
    endDrag();
    const next = moveInList(cards, from, to);
    if (next === cards) return;
    setCards(next);
    try {
      await ReorderCardsAPI(next.map((c) => c.uuid));
    } catch (err) {
      ErrorToaster(handleError(err?.status_code, err?.message));
      load();
    }
  };

  const shown = cards.map((c, i) => ({ c, i })).filter(({ c }) => !type || typeOf(c) === type);

  return (
    <div className="content">
      <style>{STYLE}</style>
      {alert}
      <Container fluid>
        <Card>
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <CardTitle tag="h2" className="mb-0">
                {type === "menu" ? t("list.titleFood") : type === "drinks" ? t("list.titleDrinks") : t("list.titleAll")}
              </CardTitle>
              <div className="d-flex align-items-center">
                <Input
                  placeholder={t("list.search")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="mr-3"
                  style={{ minWidth: 220 }}
                />
                <Button color="info" className="mb-0 mr-2" onClick={() => setAiOpen(true)}>
                  {t("list.createWithAi")}
                </Button>
                <Button color="primary" className="mb-0" onClick={() => setFormCard(null)}>
                  {t("list.newCard")}
                </Button>
              </div>
            </div>
            {cards.length > 1 && (
              <p className="text-muted small">{t("list.dragHint")}</p>
            )}

            {loading ? (
              <div className="text-center py-5"><Spinner /></div>
            ) : shown.length === 0 ? (
              <div className="text-center text-muted py-5">
                {search ? t("list.noneFound") : t("list.empty")}
              </div>
            ) : (
              <div onDrop={onDrop}>
                {shown.map(({ c, i }) => {
                  const hidden = c.is_active === false;
                  const mark = over && over.index === i ? (over.pos === "before" ? "mc-before" : "mc-after") : "";
                  return (
                    <div key={c.uuid} className={`mc-row ${mark}`} onDragOver={onRowOver(i)}>
                      <span
                        className="mc-handle"
                        title={t("list.dragTitle")}
                        draggable
                        onDragStart={(e) => {
                          dragFrom.current = i;
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", c.uuid);
                          if (e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(e.currentTarget.parentElement, 14, 14);
                        }}
                        onDragEnd={endDrag}
                      >
                        ⠿
                      </span>
                      {c.image ? (
                        <img src={imgUrl(c.image)} alt="" className={`mc-thumb ${hidden ? "mc-off" : ""}`} />
                      ) : (
                        <div className={`mc-thumb ${hidden ? "mc-off" : ""}`} />
                      )}
                      <div className={`mc-info ${hidden ? "mc-off" : ""}`}>
                        <strong>{c.name}</strong>
                        {hidden && <span className="badge badge-secondary ml-2">{t("list.hidden")}</span>}
                        <div className="text-muted small">{c.description || "-"}</div>
                        <div className="small" style={{ color: GOLD }}>
                          {t(typeOf(c) === "menu" ? "list.dishCount" : "list.wineCount", { count: c.wine_count ?? 0 })}
                        </div>
                      </div>
                      <div className="mc-actions">
                        <Button size="sm" color="primary" onClick={() => navigate(`/menu-cards/${c.uuid}/structure`)}>{t("list.structure")}</Button>
                        <Button size="sm" color="primary" onClick={() => setAssignCard(c)}>{typeOf(c) === "menu" ? t("list.dishes") : t("list.wines")}</Button>
                        <Button size="sm" color="info" onClick={() => navigate(`/menu-cards/print?type=${typeOf(c)}&card=${c.uuid}`)}>{t("list.print")}</Button>
                        <Button size="sm" color="info" onClick={() => setShareCard(shareCard?.uuid === c.uuid ? null : c)}>
                          {t("list.linkQr")}
                        </Button>
                        <Button size="sm" color="secondary" onClick={() => toggleHidden(c)}>{hidden ? t("list.show") : t("list.hide")}</Button>
                        <Button size="sm" color="warning" onClick={() => setFormCard(c)}>{t("list.edit")}</Button>
                        <Button size="sm" color="danger" onClick={() => confirmDelete(c)}>{t("list.delete")}</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>

        {shareCard && (
          <>
            {shareCard.is_active === false && (
              <p className="text-warning">
                {t("list.hiddenWarning")}
              </p>
            )}
            <EmbedCard
              title={t("list.publicCard", { name: shareCard.name })}
              pagePath={`menucard/public/${shareCard.uuid}/page`}
              widgetPath={`menucard/public/${shareCard.uuid}/widget.js`}
              hostId="risto-menu"
              label={t("list.viewMenu")}
              kind={styleKind(shareCard)}
            />
          </>
        )}
      </Container>

      <CardFormModal
        card={formCard || null}
        open={formCard !== undefined}
        onClose={() => setFormCard(undefined)}
        onSaved={load}
        kind={type === "menu" ? "food" : type === "drinks" ? "drinks" : undefined}
      />
      <AiPromptModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        title={t("list.ai.title")}
        intro={t("list.ai.intro")}
        placeholder={t("list.ai.placeholder")}
        examples={t("list.ai.examples", { returnObjects: true })}
        buttonLabel={t("list.ai.button")}
        busyLabel={t("list.ai.busy")}
        onSubmit={async (text) => {
          try {
            const res = await AICardsAPI(text, type === "menu" ? "food" : "drinks");
            successToaster(res?.message || t("list.ai.created"));
            load();
            return true;
          } catch (e) {
            ErrorToaster(handleError(e?.status_code, e?.message));
            return false;
          }
        }}
      />
      <AssignWinesModal
        card={assignCard}
        open={!!assignCard}
        onClose={() => setAssignCard(null)}
        onSaved={load}
      />
    </div>
  );
};

export default MenuCards;
